import type { StudentActivity } from "@lingoshade/domain";

export type ReportMistakeKind =
  | "incorrect"
  | "missing"
  | "extra"
  | "punctuation";
export interface ReportMistake {
  kind: ReportMistakeKind;
  expected: string;
  actual: string;
  count: number;
  saved: boolean;
}
export interface ReportMistakes {
  groups: Array<{
    kind: ReportMistakeKind;
    count: number;
    items: ReportMistake[];
  }>;
  savedWords: number;
}
function wordPart(value: string): string {
  return value
    .normalize("NFC")
    .trim()
    .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "");
}
export function studentReportMistakes(
  mistakes: StudentActivity["dictations"][number]["mistakes"],
): ReportMistakes {
  const grouped = new Map<string, ReportMistake>();
  const saved = new Set<string>();
  for (const mistake of mistakes) {
    const expectedRaw = mistake.expectedWord.normalize("NFC").trim();
    const actualRaw = mistake.actualWord.normalize("NFC").trim();
    if (!expectedRaw && !actualRaw) continue;
    const expected = wordPart(expectedRaw);
    const actual = wordPart(actualRaw);
    const kind: ReportMistakeKind =
      (!expected && !actual) ||
      (expected === actual && expectedRaw !== actualRaw)
        ? "punctuation"
        : !expected
          ? "extra"
          : !actual
            ? "missing"
            : "incorrect";
    const expectedDisplay = kind === "punctuation" ? expectedRaw : expected;
    const actualDisplay = kind === "punctuation" ? actualRaw : actual;
    const key = JSON.stringify([kind, expectedDisplay, actualDisplay]);
    const item = grouped.get(key) || {
      kind,
      expected: expectedDisplay,
      actual: actualDisplay,
      count: 0,
      saved: false,
    };
    item.count += 1;
    // Punctuation has no independent dictionary entry.
    item.saved ||= kind !== "punctuation" && mistake.addedToDictionary;
    if (expected && mistake.addedToDictionary)
      saved.add(expected.toLocaleLowerCase());
    grouped.set(key, item);
  }
  const kinds: ReportMistakeKind[] = [
    "incorrect",
    "missing",
    "extra",
    "punctuation",
  ];
  return {
    savedWords: saved.size,
    groups: kinds.flatMap((kind): ReportMistakes["groups"] => {
      const items = [...grouped.values()]
        .filter((item): boolean => item.kind === kind)
        .sort((a, b): number => b.count - a.count);
      return items.length
        ? [
            {
              kind,
              items,
              count: items.reduce(
                (total, item): number => total + item.count,
                0,
              ),
            },
          ]
        : [];
    }),
  };
}
